import { useId, useMemo, useState, type ComponentType, type ReactNode } from "react";
import { CountUp } from "@/components/CountUp";
import { createFileRoute, Link } from "@tanstack/react-router";
import { cn } from "@/lib/utils";
import {
  ArrowUpRight,
  Layers,
  Megaphone,
  Network,
  PenLine,
  Linkedin,
  Youtube,
  Facebook,
  Instagram,
  Mail,
  Rss,
  BookOpen,
  CalendarDays,
  Target,
  TrendingUp,
  Cpu,
  Globe,
  Eye,
  LayoutGrid,
  Bot,
  ShieldCheck,
  Cog,
  Plus,
} from "lucide-react";
import { XIcon } from "@/components/XIcon";
import { BlueskyIcon } from "@/components/BlueskyIcon";
import { DarkSectionBackdrop } from "@/components/DarkSectionBackdrop";
import { LightSectionBackdrop } from "@/components/LightSectionBackdrop";
import { SparkleDivider } from "@/components/SparkleDivider";
import portrait from "@/assets/portrait.webp";
import { useLinkSet, getIcon } from "@/lib/site-content";

function AllLinksGrid() {
  const items = useLinkSet("about_all_links");
  return (
    <div className="mt-12 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
      {items.map((item) => {
        const Icon = getIcon(item.icon) ?? ArrowUpRight;
        const external = item.external || item.href.startsWith("http") || item.href.startsWith("mailto:");
        const c = item.color_var ?? "var(--color-foreground)";
        return (
          <a
            key={item.item_key}
            href={item.href}
            target={external ? "_blank" : undefined}
            rel={external ? "noopener noreferrer" : undefined}
            className="group relative flex items-center gap-4 rounded-md border-2 border-foreground bg-background p-5 shadow-pop transition-all hover:-translate-y-1 hover:shadow-[0_28px_56px_-20px_oklch(0.16_0.02_280/0.4)]"
          >
            <span
              aria-hidden
              className="grid h-11 w-11 shrink-0 place-items-center rounded-full border-2"
              style={{ borderColor: c, color: c }}
            >
              <Icon className="h-5 w-5" />
            </span>
            <span className="flex-1 min-w-0">
              <span className="block font-display text-lg leading-tight">{item.label}</span>
              <span className="block truncate font-mono text-[11px] uppercase tracking-[0.18em] text-muted-foreground">
                {item.sub}
              </span>
            </span>
            <ArrowUpRight className="h-4 w-4 shrink-0 text-foreground/60 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5 group-hover:text-foreground" />
          </a>
        );
      })}
    </div>
  );
}

export const Route = createFileRoute("/about")({
  head: () => ({
    meta: [
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },

      { title: "About Scott Cunningham | Content & Social Media Strategist" },
      {
        name: "description",
        content:
          "Meet Scott Cunningham (@scottcbusiness): 10+ years in content strategy, technical copywriting and social media for AI, SaaS, cloud and Web3 brands.",
      },
      { property: "og:title", content: "About Scott Cunningham | Content & Social Media Strategist" },
      {
        property: "og:description",
        content:
          "Meet Scott Cunningham (@scottcbusiness): 10+ years in content strategy, technical copywriting and social media for AI, SaaS, cloud and Web3 brands.",
      },
      { property: "og:url", content: "https://scottcbusiness.com/about" },
      { name: "twitter:title", content: "About Scott Cunningham | Content & Social Media Strategist" },
      {
        name: "twitter:description",
        content:
          "Meet Scott Cunningham (@scottcbusiness): 10+ years in content strategy, technical copywriting and social media for AI, SaaS, cloud and Web3 brands.",
      },
      { property: "og:image", content: "https://scottcbusiness.com/og-default.jpg" },
      { property: "og:image:alt", content: "Scott Cunningham, Content and Social Media Strategy" },
        { name: "twitter:image:alt", content: "Scott Cunningham, Content and Social Media Strategy" },
      { name: "twitter:image", content: "https://scottcbusiness.com/og-default.jpg" },
    ],
    links: [
      { rel: "canonical", href: "https://scottcbusiness.com/about" },
      { rel: "preload", as: "image", href: portrait, fetchPriority: "high" as const },
    ],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "ProfilePage",
          url: "https://scottcbusiness.com/about",
          name: "About Scott Cunningham (@scottcbusiness)",
          mainEntity: {
            "@type": "Person",
            "@id": "https://scottcbusiness.com/#person",
            name: "Scott Cunningham",
            alternateName: ["scottcbusiness", "@scottcbusiness", "Scott Cunningham Services"],
            url: "https://scottcbusiness.com/",
            jobTitle: "Content Strategist",
            sameAs: [
              "https://www.linkedin.com/in/scottcbusiness",
              "https://x.com/scottcbusiness",
              "https://www.youtube.com/@scottcbusiness",
              "https://bsky.app/profile/scottcbusiness.com",
            ],
          },
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Home", item: "https://scottcbusiness.com/" },
            {
              "@type": "ListItem",
              position: 2,
              name: "About",
              item: "https://scottcbusiness.com/about",
            },
          ],
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: ABOUT_FAQ.map((f) => ({
            "@type": "Question",
            name: f.q,
            acceptedAnswer: { "@type": "Answer", text: f.a },
          })),
        }),
      },
    ],
  }),
  component: AboutPage,
});

const faqLinkClass =
  "text-gradient font-medium underline decoration-[var(--color-electric)] underline-offset-4 transition-colors hover:decoration-[var(--color-primary)]";

function FaqExternalLink({ href, children }: { href: string; children: ReactNode }) {
  return (
    <a href={href} target="_blank" rel="noopener noreferrer" className={faqLinkClass}>
      {children}
    </a>
  );
}

const ABOUT_FAQ: { q: string; a: string; aJsx?: ReactNode }[] = [
  {
    q: "Who is Scott Cunningham?",
    a: "Scott Cunningham (@scottcbusiness) is a Toronto-based content strategist and social media lead. He helps AI, cloud, Web3, cybersecurity, SaaS, and consumer tech brands turn complex ideas into clear content systems and measurable growth.",
    aJsx: (
      <>
        Scott Cunningham (<FaqExternalLink href="https://x.com/scottcbusiness">@scottcbusiness</FaqExternalLink>) is a
        Toronto-based content strategist and social media lead. He helps AI, cloud, Web3, cybersecurity, SaaS, and
        consumer tech brands turn complex ideas into clear content systems and measurable growth.
      </>
    ),
  },
  {
    q: "What does Scott Cunningham do?",
    a: "Scott Cunningham Services covers content strategy, technical copywriting, social media and community growth, and AI-assisted content workflows. He works as a fractional lead, embedded collaborator, or project consultant, and ties reporting to pipeline, conversion, and retention.",
    aJsx: (
      <>
        <Link to="/services" className={faqLinkClass}>
          Scott Cunningham Services
        </Link>{" "}
        covers content strategy, technical copywriting, social media and community growth, and AI-assisted content
        workflows. He works as a fractional lead, embedded collaborator, or project consultant, and ties reporting to
        pipeline, conversion, and retention.
      </>
    ),
  },
  {
    q: "When did Scott start his career in content and social media?",
    a: "Scott has more than 10 years of experience. Over that time he has managed 120+ accounts for 57+ clients, including CUDO Compute, Intel, Thunderbolt Technologies, Rocklands Entertainment, and Waypoint Convenience.",
    aJsx: (
      <>
        Scott has more than 10 years of experience. Over that time he has managed 120+ accounts for 57+ clients,
        including CUDO Compute, Intel, Thunderbolt Technologies, Rocklands Entertainment, and Waypoint Convenience. You
        can see selected case studies on the{" "}
        <Link to="/work" className={faqLinkClass}>
          work page
        </Link>
        .
      </>
    ),
  },
  {
    q: "What is Scott Cunningham's podcast?",
    a: "Scott founded, hosted, and produced The Tech & Things Show from 2018 to 2024. It ran 550+ episodes on blockchain, Web3, AI, privacy, and decentralization, with guests like John McAfee, Andreas Antonopoulos, and Naomi Brockwell.",
    aJsx: (
      <>
        Scott founded, hosted, and produced{" "}
        <Link to="/media" className={faqLinkClass}>
          The Tech &amp; Things Show
        </Link>{" "}
        from 2018 to 2024. It ran 550+ episodes on blockchain, Web3, AI, privacy, and decentralization, with guests like
        John McAfee, Andreas Antonopoulos, and Naomi Brockwell.
      </>
    ),
  },
  {
    q: "What is Scott Cunningham's social media handle?",
    a: "Scott's handle is @scottcbusiness on X, LinkedIn, YouTube, and Bluesky (scottcbusiness.com). His website is scottcbusiness.com.",
    aJsx: (
      <>
        Scott's handle is @scottcbusiness on <FaqExternalLink href="https://x.com/scottcbusiness">X</FaqExternalLink>,{" "}
        <FaqExternalLink href="https://www.linkedin.com/in/scottcbusiness">LinkedIn</FaqExternalLink>,{" "}
        <FaqExternalLink href="https://www.youtube.com/@scottcbusiness">YouTube</FaqExternalLink>, and{" "}
        <FaqExternalLink href="https://bsky.app/profile/scottcbusiness.com">Bluesky</FaqExternalLink>. His website is{" "}
        <FaqExternalLink href="https://scottcbusiness.com">scottcbusiness.com</FaqExternalLink>.
      </>
    ),
  },
  {
    q: "How can I hire Scott Cunningham?",
    a: "Use the contact form to start a conversation about a role or project. Pricing is shared after a short scoping call.",
    aJsx: (
      <>
        Use the{" "}
        <Link to="/contact" className={faqLinkClass}>
          contact form
        </Link>{" "}
        to start a conversation about a role or project. Pricing is shared after a short scoping call.
      </>
    ),
  },
];

type TopicCategory = "Technology" | "Content" | "Themes" | "Markets";

const TOPICS: { label: string; categories: TopicCategory[]; focus?: boolean }[] = [
  // (topic map data — retained for reference; the current about page renders
  // the DISCIPLINE_GROUPS grouping below instead of the interactive cluster.)
  { label: "AI", categories: ["Technology"], focus: true },
  { label: "Cloud", categories: ["Technology"], focus: true },
  { label: "GPU Compute", categories: ["Technology"] },
  { label: "Web3", categories: ["Technology"], focus: true },
  { label: "Crypto", categories: ["Technology"] },
  { label: "Cybersecurity", categories: ["Technology"], focus: true },
  { label: "Privacy", categories: ["Themes"] },
  { label: "Decentralization", categories: ["Themes"] },
  { label: "SaaS", categories: ["Technology", "Markets"] },
  { label: "Social Media", categories: ["Content"] },
  { label: "Creator Economy", categories: ["Content", "Markets"] },
  { label: "Content Strategy", categories: ["Content"], focus: true },
  { label: "Community", categories: ["Content"] },
  { label: "Digital Trust", categories: ["Themes"] },
  { label: "Automation", categories: ["Themes"] },
  { label: "Infrastructure", categories: ["Technology"] },
  { label: "Podcasting", categories: ["Content"] },
  { label: "Blockchain", categories: ["Technology"] },
  { label: "Online Platforms", categories: ["Markets"] },
  { label: "Marketing Analytics", categories: ["Content"] },
  { label: "Technical Copywriting", categories: ["Content"] },
  { label: "Sovereign Compute", categories: ["Technology"] },
  { label: "LLMs", categories: ["Technology"] },
  { label: "Platform Risk", categories: ["Themes"] },
  { label: "Newsletters", categories: ["Content"] },
  { label: "Developer Content", categories: ["Content"] },
  { label: "Enterprise Tech", categories: ["Markets"] },
];

const CATEGORY_FILTERS: { key: "All" | TopicCategory; description: string }[] = [
  { key: "All", description: "Everything I work across" },
  {
    key: "Technology",
    description: "AI, cloud, GPU compute, cybersecurity, Web3, blockchain, SaaS, infrastructure",
  },
  {
    key: "Content",
    description: "Strategy, copywriting, newsletters, social, podcasting, video, developer content",
  },
  {
    key: "Themes",
    description: "Privacy, decentralization, digital trust, platform risk, automation",
  },
  {
    key: "Markets",
    description: "Startups, enterprise tech, consumer tech, retail, entertainment",
  },
];

// Grouped disciplines shown on the About page. Six categories, each a
// short list of the specific work I do inside that lane.
const DISCIPLINE_GROUPS: {
  name: string;
  items: string[];
  icon: ComponentType<{ className?: string }>;
  color: string;
  size: "wide" | "normal";
}[] = [
  {
    name: "Strategy & Messaging",
    items: [
      "Positioning",
      "Narrative",
      "Messaging frameworks",
      "Editorial strategy",
      "Launch planning",
    ],
    icon: Target,
    color: "var(--color-primary)",
    size: "wide",
  },
  {
    name: "Writing & Production",
    items: ["Long-form articles", "Technical writing", "Copywriting", "Ghostwriting", "Editing"],
    icon: PenLine,
    color: "var(--color-electric)",
    size: "normal",
  },
  {
    name: "Social & Community",
    items: [
      "Social media strategy",
      "Community growth",
      "Executive presence",
      "Content repurposing",
      "Podcast & interviews",
    ],
    icon: Network,
    color: "var(--color-violet)",
    size: "normal",
  },
  {
    name: "Growth & Performance",
    items: ["Paid media", "Newsletters", "Website copy", "Analytics", "Marketing audits"],
    icon: TrendingUp,
    color: "var(--color-pink)",
    size: "wide",
  },
  {
    name: "AI & Systems",
    items: [
      "AI-assisted workflows",
      "Editorial tooling",
      "Prompt design",
      "Automation",
      "Custom GPTs",
    ],
    icon: Cpu,
    color: "var(--color-electric)",
    size: "normal",
  },
  {
    name: "Technical Markets",
    items: [
      "AI & ML",
      "Cybersecurity",
      "Web3 & crypto",
      "Cloud & GPU compute",
      "SaaS & developer tools",
    ],
    icon: Globe,
    color: "var(--color-primary)",
    size: "normal",
  },
];

// Mobile single-column order: Technical Markets is moved before Growth & Performance
// so the primary colour does not repeat twice in the scrolling loop.
const MOBILE_DISCIPLINE_GROUPS: typeof DISCIPLINE_GROUPS = [
  DISCIPLINE_GROUPS[0],
  DISCIPLINE_GROUPS[1],
  DISCIPLINE_GROUPS[2],
  DISCIPLINE_GROUPS[5],
  DISCIPLINE_GROUPS[3],
  DISCIPLINE_GROUPS[4],
];

const WINS = [
  {
    icon: Layers,
    title: "Built content systems for technical infrastructure",
    body: "Led content, editorial, and communications across AI infrastructure, GPU compute, HPC, sovereign cloud, and permissionless Web3 compute. Turned complex technical topics into buyer-facing narratives across blogs, whitepapers, newsletters, landing pages, tutorials, sales materials, and social campaigns.",
    accent: "var(--color-primary)",
  },
  {
    icon: Megaphone,
    title: "Scaled multi-platform creator reach",
    body: "Built Tech & Things and @ScottCBusiness into a long-form content platform with 550+ episodes focused on blockchain, Web3, decentralization, AI, privacy, cybersecurity, and emerging technology.",
    accent: "var(--color-electric)",
  },
  {
    icon: Network,
    title: "Managed complex client and campaign ecosystems",
    body: "Managed 120+ accounts and supported 57+ clients across multiple sectors, combining content strategy, social media, community management, campaign execution, analytics, technical copywriting, and AI-assisted workflows.",
    accent: "var(--color-violet)",
  },
  {
    icon: PenLine,
    title: "Turned technical subjects into clear public narratives",
    body: "Produced explainers, scripts, articles, podcast episodes, newsletters, and campaigns across AI, Web3, cybersecurity, decentralization, privacy, cloud infrastructure, social media, platform risk, and digital trust.",
    accent: "var(--color-pink)",
  },
];

const STATS: { v: string; l: string; note?: string }[] = [
  { v: "10+", l: "Years in content, social, and digital marketing" },
  { v: "120+", l: "Accounts managed" },
  { v: "57+", l: "Clients supported" },
  { v: "550+", l: "Podcast / video episodes produced" },
  { v: "22.7M+", l: "Personal brand impressions" },
  {
    v: "$1M → $100M",
    l: "Scale-up period supported",
    note: "Supported through marketing, content, messaging, and GTM execution during a major company growth period.",
  },
];

const PRINCIPLES = [
  {
    t: "Clear over clever",
    b: "If the audience does not understand it, the content is not working.",
    icon: Eye,
    color: "var(--color-primary)",
  },
  {
    t: "Systems over one-offs",
    b: "A good content engine should make publishing easier, not more chaotic.",
    icon: Cog,
    color: "var(--color-electric)",
  },
  {
    t: "AI-assisted, not AI-replaced",
    b: "AI should improve the workflow, not replace the thinking.",
    icon: Bot,
    color: "var(--color-violet)",
  },
  {
    t: "Trust over vanity metrics",
    b: "Reach matters, but trust is what turns attention into opportunity.",
    icon: ShieldCheck,
    color: "var(--color-pink)",
  },
];

function AboutPage() {
  const [activeFilter, setActiveFilter] = useState<"All" | TopicCategory>("All");
  const visibleTopics = useMemo(
    () =>
      TOPICS.map((t) => ({
        ...t,
        active: activeFilter === "All" || t.categories.includes(activeFilter as TopicCategory),
      })),
    [activeFilter],
  );

  return renderAbout({ activeFilter, setActiveFilter, visibleTopics });
}

function HowIWorkOrbit() {
  const positions = [
    { left: "lg:left-[50%]", top: "lg:top-[18%]" },
    { left: "lg:left-[82%]", top: "lg:top-[50%]" },
    { left: "lg:left-[50%]", top: "lg:top-[82%]" },
    { left: "lg:left-[18%]", top: "lg:top-[50%]" },
  ];

  return (
    <div className="relative mx-auto mt-14 w-full max-w-[880px] lg:mt-24">
      {/* Desktop orbit decorative layer */}
      <div className="relative hidden aspect-square w-full overflow-visible lg:block">
        {/* Animated gradient wave ring */}
        <svg
          aria-hidden
          className="pointer-events-none absolute inset-0 h-full w-full"
          viewBox="0 0 100 100"
          preserveAspectRatio="xMidYMid meet"
        >
          <defs>
            <linearGradient id="how-wave" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="var(--color-primary)" stopOpacity="0.8" />
              <stop offset="50%" stopColor="var(--color-electric)" stopOpacity="0.6" />
              <stop offset="100%" stopColor="var(--color-violet)" stopOpacity="0.8" />
            </linearGradient>
            <radialGradient id="how-center" cx="50%" cy="50%" r="50%">
              <stop offset="0%" stopColor="var(--color-electric)" stopOpacity="0.4" />
              <stop offset="100%" stopColor="var(--color-violet)" stopOpacity="0" />
            </radialGradient>
          </defs>
          <circle
            cx="50"
            cy="50"
            r="30"
            fill="url(#how-center)"
            opacity={0.9}
            style={{ transition: "opacity 1s ease" }}
          />
          <g style={{ transformOrigin: "50% 50%", animation: "spin-slow 60s linear infinite" }}>
            <circle
              cx="50"
              cy="50"
              r="36"
              fill="none"
              stroke="url(#how-wave)"
              strokeWidth="0.35"
              strokeDasharray="2 1.6"
              opacity="0.75"
            />
          </g>
          <g
            style={{ transformOrigin: "50% 50%", animation: "spin-slow 90s linear infinite reverse" }}
          >
            <circle
              cx="50"
              cy="50"
              r="30"
              fill="none"
              stroke="url(#how-wave)"
              strokeWidth="0.22"
              strokeDasharray="1 2"
              opacity="0.55"
            />
          </g>
          {PRINCIPLES.map((p, i) => {
            const angle = (i * 90 - 90) * (Math.PI / 180);
            const r = 32;
            const x = 50 + Math.cos(angle) * r;
            const y = 50 + Math.sin(angle) * r;
            const ctrl = 8;
            const cx = (50 + x) / 2 + Math.cos(angle + Math.PI / 2) * ctrl;
            const cy = (50 + y) / 2 + Math.sin(angle + Math.PI / 2) * ctrl;
            return (
              <path
                key={i}
                d={`M 50 50 Q ${cx} ${cy} ${x} ${y}`}
                fill="none"
                stroke={p.color}
                strokeWidth="0.35"
                strokeDasharray="1.2 1.6"
                opacity="0.65"
                style={{ transition: "d 1.2s ease" }}
              >
                <animate
                  attributeName="stroke-dashoffset"
                  from="0"
                  to="28"
                  dur="9s"
                  repeatCount="indefinite"
                />
              </path>
            );
          })}
        </svg>

        {/* Center hub */}
        <div className="absolute left-1/2 top-1/2 z-10 flex h-48 w-48 -translate-x-1/2 -translate-y-1/2 flex-col items-center justify-center rounded-full border-2 border-white/30 bg-ink/80 shadow-[0_0_70px_-12px_var(--color-electric)] backdrop-blur-xl">
          <LayoutGrid className="h-12 w-12 text-white" />
          <span className="mt-3 text-center font-mono text-sm font-semibold uppercase tracking-[0.15em] text-white">
            Content
            <br />
            systems
          </span>
        </div>
      </div>

      {/* Mobile timeline track */}
      <div
        aria-hidden
        className="absolute left-8 top-0 bottom-0 w-px lg:hidden"
        style={{
          background:
            "linear-gradient(180deg, transparent, rgba(255,255,255,0.3) 15%, rgba(255,255,255,0.3) 85%, transparent)",
        }}
      />

      {/* Principles - single source, responsive layout */}
      <div className="relative space-y-6 lg:absolute lg:inset-0">
        {PRINCIPLES.map((p, i) => {
          const Icon = p.icon;
          const pos = positions[i];
          return (
            <div
              key={p.t}
              className={cn(
                "relative z-10 flex items-start gap-6",
                "lg:absolute lg:z-20 lg:h-[300px] lg:w-[300px] lg:-translate-x-1/2 lg:-translate-y-1/2 lg:items-center lg:justify-center",
                pos.left,
                pos.top
              )}
              style={
                {
                  "--p-color": p.color,
                  "--p-anim": `float ${6.5 + i * 0.7}s ease-in-out ${i * -0.6}s infinite`,
                } as React.CSSProperties
              }
            >
              {/* Timeline dot - mobile only */}
              <div
                aria-hidden
                className="absolute left-8 top-6 z-10 flex h-5 w-5 -translate-x-1/2 items-center justify-center rounded-full border-2 bg-ink lg:hidden"
                style={{ borderColor: p.color }}
              >
                <span className="h-2 w-2 rounded-full" style={{ background: p.color }} />
              </div>

              {/* Card / bubble */}
              <div
                className={cn(
                  "group relative flex-1 rounded-md border-2 bg-ink/60 p-6 shadow-pop transition-all duration-300 hover:-translate-y-1",
                  "lg:flex lg:h-full lg:w-full lg:flex-col lg:items-center lg:justify-center lg:rounded-full lg:p-8 lg:text-center lg:backdrop-blur-xl lg:hover:translate-y-0 lg:hover:scale-[1.1]",
                  "lg:bg-[radial-gradient(circle_at_50%_25%,_color-mix(in_oklab,var(--p-color)_22%,_oklch(0.14_0.02_280_/_0.92)),_oklch(0.12_0.02_280_/_0.96))]",
                  "lg:shadow-[0_0_30px_-8px_color-mix(in_oklab,var(--p-color)_55%,_transparent),_inset_0_1px_0_oklch(1_0_0_/_0.12)]",
                  "lg:animate-[var(--p-anim)]"
                )}
                style={{ borderColor: p.color }}
              >
                {/* Glow overlay - desktop only */}
                <div
                  aria-hidden
                  className="pointer-events-none absolute inset-0 hidden rounded-full opacity-60 transition-opacity duration-500 group-hover:opacity-100 lg:block"
                  style={{ boxShadow: `0 0 40px -4px ${p.color}, inset 0 0 40px -10px ${p.color}` }}
                />

                <div className="flex items-start gap-4 lg:-translate-y-4 lg:flex-col lg:items-center lg:gap-2">
                  <span
                    className={cn(
                      "grid h-10 w-10 shrink-0 place-items-center rounded-md border-2 transition-transform duration-300 group-hover:scale-110",
                      "lg:h-16 lg:w-16 lg:rounded-full"
                    )}
                    style={{
                      borderColor: p.color,
                      color: p.color,
                      background: `color-mix(in oklab, ${p.color} 22%, transparent)`,
                    }}
                  >
                    <Icon className="h-5 w-5 lg:h-9 lg:w-9" />
                  </span>
                  <div className="min-w-0 lg:flex lg:flex-col lg:items-center">
                    <h3 className="mt-0 text-center font-display text-lg leading-snug text-white lg:mt-2 lg:max-w-[95%] lg:text-[1.65rem] lg:font-bold lg:leading-tight">
                      {p.t}
                    </h3>
                    <p className="mt-2 text-center text-sm leading-relaxed text-white/70 lg:mt-2 lg:max-w-[95%] lg:px-1 lg:text-sm lg:leading-snug lg:text-white">
                      {p.b}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function HowIWorkBackdrop() {
  const uid = useId().replace(/:/g, "");
  const ringGradient = `hw-ring-${uid}`;
  const dotPattern = `hw-dots-${uid}`;
  return (
    <div className="pointer-events-none absolute inset-0 -z-10 overflow-hidden" aria-hidden="true">
      {/* Central glow behind the orbit */}
      <div
        className="absolute left-1/2 top-[55%] aspect-square w-[110%] max-w-[1200px] -translate-x-1/2 -translate-y-1/2 rounded-full opacity-[0.18] blur-3xl"
        style={{
          background:
            "radial-gradient(circle, color-mix(in oklab, var(--color-electric) 60%, transparent) 0%, color-mix(in oklab, var(--color-violet) 35%, transparent) 55%, transparent 80%)",
        }}
      />

      {/* Rotating concentric rings behind the orbit */}
      <svg
        className="absolute left-1/2 top-[55%] w-[90%] max-w-[1000px] -translate-x-1/2 -translate-y-1/2 opacity-[0.15] animate-[spin-slow_120s_linear_infinite]"
        viewBox="0 0 400 400"
        fill="none"
        preserveAspectRatio="xMidYMid meet"
      >
        <defs>
          <linearGradient id={ringGradient} x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stopColor="var(--color-primary)" stopOpacity="0.5" />
            <stop offset="50%" stopColor="var(--color-electric)" stopOpacity="0.3" />
            <stop offset="100%" stopColor="var(--color-violet)" stopOpacity="0.5" />
          </linearGradient>
        </defs>
        <circle cx="200" cy="200" r="190" stroke={`url(#${ringGradient})`} strokeWidth="0.5" strokeDasharray="4 6" />
        <circle
          cx="200"
          cy="200"
          r="150"
          stroke={`url(#${ringGradient})`}
          strokeWidth="0.35"
          strokeDasharray="2 8"
          opacity="0.6"
        />
        <circle
          cx="200"
          cy="200"
          r="110"
          stroke={`url(#${ringGradient})`}
          strokeWidth="0.25"
          strokeDasharray="1 10"
          opacity="0.4"
        />
      </svg>

      {/* Subtle dot field */}
      <svg className="absolute inset-0 h-full w-full opacity-[0.14]" aria-hidden="true">
        <defs>
          <pattern id={dotPattern} x="0" y="0" width="48" height="48" patternUnits="userSpaceOnUse">
            <circle cx="2" cy="2" r="1" fill="var(--color-cream)" opacity="0.6" />
            <circle cx="26" cy="26" r="1.2" fill="var(--color-cream)" opacity="0.3" />
          </pattern>
        </defs>
        <rect width="100%" height="100%" fill={`url(#${dotPattern})`} />
      </svg>

      {/* Diagonal accent lines */}
      <svg
        className="absolute inset-0 h-full w-full opacity-[0.08]"
        viewBox="0 0 100 100"
        preserveAspectRatio="none"
        aria-hidden="true"
      >
        <line x1="0" y1="100" x2="100" y2="0" stroke="var(--color-cream)" strokeWidth="0.1" strokeDasharray="3 4" />
        <line x1="0" y1="0" x2="100" y2="100" stroke="var(--color-cream)" strokeWidth="0.1" strokeDasharray="3 4" />
      </svg>

      {/* Floating sparkles */}
      <span className="absolute left-[10%] top-[38%] text-2xl text-white/50 animate-pulse">✦</span>
      <span className="absolute left-[88%] top-[55%] text-xl text-primary/60 animate-pulse [animation-delay:400ms]">✦</span>
      <span className="absolute left-[5%] top-[72%] text-lg text-electric/60 animate-pulse [animation-delay:800ms]">✦</span>
      <span className="absolute right-[12%] top-[36%] text-lg text-violet/60 animate-pulse [animation-delay:1200ms]">✦</span>
      <span className="absolute right-[6%] bottom-[18%] text-2xl text-white/55 animate-pulse [animation-delay:1600ms]">✦</span>
    </div>
  );
}

type VisibleTopic = (typeof TOPICS)[number] & { active: boolean };

function computePositions(n: number): { x: number; y: number }[] {

  // Phyllotaxis (sunflower) distribution. The golden angle gives an even
  // spread with no two points sharing an angle, so adjacent labels don't
  // stack on top of each other the way concentric rings can.
  const golden = Math.PI * (3 - Math.sqrt(5));
  const pts: { x: number; y: number }[] = [];
  for (let i = 0; i < n; i++) {
    // skip i=0 from origin so nothing sits dead-center under another bubble
    const r = Math.sqrt((i + 0.6) / n);
    const theta = i * golden;
    pts.push({
      x: 50 + Math.cos(theta) * r * 44, // wider X
      y: 50 + Math.sin(theta) * r * 40, // slightly squashed Y
    });
  }
  return pts;
}

function TopicCluster({ topics }: { topics: VisibleTopic[] }) {
  const positions = useMemo(() => computePositions(topics.length), [topics.length]);
  const connections = useMemo(() => {
    const lines: { i: number; j: number; d: number }[] = [];
    for (let i = 0; i < positions.length; i++) {
      for (let j = i + 1; j < positions.length; j++) {
        const dx = positions[i].x - positions[j].x;
        const dy = positions[i].y - positions[j].y;
        const d = Math.hypot(dx, dy);
        if (d < 16) lines.push({ i, j, d });
      }
    }
    return lines;
  }, [positions]);

  return (
    <div className="@container relative mt-10 overflow-hidden rounded-lg border-2 border-foreground bg-background/70 shadow-pop backdrop-blur-xl">
      <div className="relative h-[420px] w-full sm:h-[500px] md:h-[580px] lg:h-[620px]">
        {/* translucent blurred backdrop */}
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0"
          style={{
            background:
              "radial-gradient(40% 50% at 20% 20%, color-mix(in oklab, var(--color-primary) 35%, transparent), transparent 70%), radial-gradient(45% 55% at 80% 30%, color-mix(in oklab, var(--color-electric) 30%, transparent), transparent 70%), radial-gradient(50% 50% at 50% 90%, color-mix(in oklab, var(--color-violet) 28%, transparent), transparent 70%)",
            filter: "blur(40px)",
          }}
        />
        {/* matrix grid + connector lines */}
        <svg
          aria-hidden
          className="pointer-events-none absolute inset-0 h-full w-full"
          xmlns="http://www.w3.org/2000/svg"
          preserveAspectRatio="none"
          viewBox="0 0 100 100"
        >
          <defs>
            <pattern id="topic-grid" width="4" height="4" patternUnits="userSpaceOnUse">
              <path d="M 4 0 L 0 0 0 4" fill="none" stroke="currentColor" strokeWidth="0.08" />
            </pattern>
            <linearGradient id="topic-line" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="var(--color-primary)" stopOpacity="0.55" />
              <stop offset="50%" stopColor="var(--color-electric)" stopOpacity="0.4" />
              <stop offset="100%" stopColor="var(--color-violet)" stopOpacity="0.55" />
            </linearGradient>
          </defs>
          <rect
            width="100"
            height="100"
            fill="url(#topic-grid)"
            className="text-foreground"
            opacity="0.18"
          />
          <g stroke="url(#topic-line)" strokeWidth="0.12" fill="none">
            {connections.map(({ i, j }) => {
              const a = positions[i];
              const b = positions[j];
              const active = topics[i].active && topics[j].active;
              return (
                <line
                  key={`${i}-${j}`}
                  x1={a.x}
                  y1={a.y}
                  x2={b.x}
                  y2={b.y}
                  opacity={active ? 0.9 : 0.25}
                />
              );
            })}
          </g>
          {/* node dots under bubbles */}
          <g>
            {positions.map((p, i) => (
              <circle
                key={i}
                cx={p.x}
                cy={p.y}
                r="0.35"
                fill="currentColor"
                className="text-foreground"
                opacity={topics[i].active ? 0.45 : 0.15}
              />
            ))}
          </g>
        </svg>
        {/* bubbles */}
        {topics.map((t, i) => {
          const palette = [
            "var(--color-primary)",
            "var(--color-electric)",
            "var(--color-violet)",
            "var(--color-pink)",
          ];
          const c = palette[i % palette.length];
          const p = positions[i] ?? { x: 50, y: 50 };
          const size = t.focus
            ? "text-[clamp(10px,1.55cqw,14px)] px-[clamp(0.6rem,1.4cqw,1rem)] py-[clamp(0.35rem,1cqw,0.55rem)]"
            : "text-[clamp(8px,1.25cqw,12px)] px-[clamp(0.45rem,1.1cqw,0.8rem)] py-[clamp(0.2rem,0.8cqw,0.4rem)]";
          return (
            <span
              key={t.label}
              className={
                "absolute -translate-x-1/2 -translate-y-1/2 whitespace-nowrap rounded-full border-2 font-mono uppercase tracking-[0.14em] backdrop-blur-md transition-all duration-300 will-change-transform " +
                size +
                (t.active
                  ? " bg-background/85 shadow-[inset_0_1px_0_oklch(1_0_0/0.7),0_8px_24px_-6px_oklch(0.16_0.02_280/0.28)] hover:-translate-x-1/2 hover:-translate-y-[calc(50%+4px)] hover:shadow-[inset_0_1px_0_oklch(1_0_0/0.8),0_18px_36px_-10px_oklch(0.16_0.02_280/0.45)]"
                  : " border-foreground/30 bg-background/40 text-foreground")
              }
              style={{
                left: `${p.x}%`,
                top: `${p.y}%`,
                animation: t.active ? `float ${7 + (i % 5)}s ease-in-out infinite` : undefined,
                animationDelay: `${(i % 7) * -0.7}s`,
                borderColor: t.active ? c : undefined,
                color: t.active ? c : undefined,
              }}
            >
              {t.label}
            </span>
          );
        })}
      </div>
    </div>
  );
}

function renderAbout({
  activeFilter,
  setActiveFilter,
  visibleTopics,
}: {
  activeFilter: "All" | TopicCategory;
  setActiveFilter: (k: "All" | TopicCategory) => void;
  visibleTopics: VisibleTopic[];
}) {
  return (
    <>
      {/* HERO */}
      <section className="relative isolate overflow-hidden grain">
        <LightSectionBackdrop variant="dots" arcs={false} />
        <div className="blob float-slow -left-32 -top-24 h-[420px] w-[420px] bg-[var(--color-primary)]" />
        <div
          className="blob float-slow right-[-120px] top-40 h-[360px] w-[360px] bg-[var(--color-electric)]"
          style={{ animationDelay: "-3s" }}
        />
        <div className="mx-auto grid max-w-7xl gap-12 px-6 py-20 lg:grid-cols-12 lg:px-10 lg:py-28">
          <div className="lg:col-span-5">
            <div className="relative">
              <div className="absolute -inset-3 -z-10 rotate-2 bg-gradient-acid hidden xl:block" />
              <div className="absolute -bottom-4 -right-4 -z-10 h-32 w-32 rounded-full bg-[var(--color-electric)] blur-2xl opacity-70 hidden xl:block" />
              <div className="group relative mx-auto aspect-square w-3/4 max-w-[630px] overflow-hidden rounded-full transition-all duration-300 ease-out hover:-translate-y-1 hover:scale-105 xl:mx-0 xl:aspect-[4/5] xl:w-full xl:max-w-none xl:rounded-sm xl:border-2 xl:border-foreground xl:shadow-pop xl:hover:scale-100 xl:hover:shadow-[0_28px_56px_-20px_oklch(0.16_0.02_280/0.5)]">
                <img
                  src={portrait}
                  alt="Portrait of Scott Cunningham, social media and content lead based in Toronto"
                  className="h-full w-full object-cover transition-transform duration-500 ease-out xl:group-hover:scale-[1.04]"
                  width={1024}
                  height={1280}
                  loading="eager"
                  fetchPriority="high"
                  decoding="sync"
                />
              </div>
            </div>
            <div className="mt-6 flex flex-wrap justify-center gap-3">
              <Link
                to="/contact"
                className="group inline-flex items-center gap-2 rounded-md border-2 border-foreground bg-background px-4 py-2.5 font-mono text-xs uppercase tracking-[0.2em] transition-all hover:-translate-y-0.5 hover:bg-foreground hover:text-background"
              >
                Get in touch
                <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </Link>
              <Link
                to="/resume"
                className="group inline-flex items-center gap-2 rounded-md border-2 border-foreground bg-background px-4 py-2.5 font-mono text-xs uppercase tracking-[0.2em] transition-all hover:-translate-y-0.5 hover:bg-foreground hover:text-background"
              >
                Resume
                <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </Link>
            </div>
          </div>
          <div className="relative lg:col-span-7">
            <p className="font-mono text-xs uppercase tracking-[0.25em] text-muted-foreground">
              About Scott Cunningham
            </p>
            <h1 className="mt-6 font-display text-[clamp(2.5rem,6vw,5rem)] leading-[1]">
              Scott Cunningham<br />
              <em className="italic text-gradient">Content &amp; social media strategist.</em>
            </h1>
            <div className="mt-10 space-y-6 text-lg leading-relaxed text-muted-foreground">
              <p>
                I'm Scott Cunningham (@scottcbusiness), a Toronto-based content strategist and social media lead.
                I help AI, SaaS, cloud, Web3, and cybersecurity brands explain complex products
                through content strategy, technical copywriting, and social media. As a fractional
                lead, embedded collaborator, or project consultant, I connect clear messaging with
                editorial systems and AI-assisted workflows so teams can publish consistently
                and build trust with their audiences.
              </p>
              <p>
                Across 10+ years in digital marketing, I've managed 120+ accounts, supported 57+
                clients, led content for GPU infrastructure and Web3 compute, built a 550+ episode
                creator platform, and helped teams move from scattered content to systems that
                support trust, growth, and measurable outcomes.
              </p>
              <p>
                Need a fractional content lead or support with a specific project?{' '}
                <Link to="/services" className={faqLinkClass}>Explore my services</Link>{' '}
                or <Link to="/contact" className={faqLinkClass}>tell me about your project</Link>.
              </p>
            </div>
            <div className="mt-8 flex flex-wrap gap-2">
              {["Strategy", "Copywriting", "Community", "AI Workflows", "Technical Content"].map(
                (tag, i) => {
                  const palette = [
                    "var(--color-primary)",
                    "var(--color-electric)",
                    "var(--color-violet)",
                    "var(--color-pink)",
                    "var(--color-foreground)",
                  ];
                  const c = palette[i % palette.length];
                  return (
                    <span
                      key={tag}
                      className="rounded-full border-2 bg-background px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.2em]"
                      style={{ borderColor: c, color: c }}
                    >
                      {tag}
                    </span>
                  );
                },
              )}
            </div>
          </div>
        </div>
      </section>

      <SparkleDivider />

      {/* SELECTED WINS */}
      <section className="relative isolate overflow-hidden border-b border-border">
        <LightSectionBackdrop variant="analytics" arcs={false} sparkles={false} />
        <div className="relative mx-auto max-w-7xl px-6 py-20 lg:px-10 lg:py-28">
          <div className="grid gap-10 md:grid-cols-12">
            <div className="md:col-span-5">
              <p className="font-mono text-xs uppercase tracking-[0.25em] text-muted-foreground">
                Selected Wins
              </p>
              <h2 className="mt-4 font-display text-4xl leading-[1.05] md:text-5xl">
                The pattern behind the <em className="italic text-gradient">work.</em>
              </h2>
            </div>
            <p className="md:col-span-7 text-lg leading-relaxed text-muted-foreground md:pt-12">
              The work has changed across industries, but the pattern has stayed the same: clarify
              the message, build the system, ship the content, and connect it to outcomes.
            </p>
          </div>
          <div className="mt-14 grid gap-6 md:grid-cols-2">
            {WINS.map(({ icon: Icon, title, body, accent }) => (
              <article
                key={title}
                className="group relative overflow-hidden rounded-md border-2 border-foreground bg-background p-7 shadow-pop transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_28px_56px_-20px_oklch(0.16_0.02_280/0.4)]"
              >
                <div
                  aria-hidden
                  className="absolute -left-1 top-0 h-full w-1.5 transition-all duration-300 group-hover:w-2"
                  style={{ background: `linear-gradient(180deg, ${accent}, transparent)` }}
                />
                <div
                  className="grid h-11 w-11 place-items-center rounded-md border-2 border-foreground"
                  style={{ background: accent, color: "var(--color-background)" }}
                >
                  <Icon className="h-5 w-5" />
                </div>
                <h3 className="mt-5 font-display text-2xl leading-tight">{title}</h3>
                <p className="mt-3 text-[15px] leading-relaxed text-muted-foreground">{body}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* TRACK RECORD */}
      <section className="relative isolate overflow-hidden bg-gradient-night text-white">
        <DarkSectionBackdrop />
        <div className="relative mx-auto max-w-7xl px-6 py-20 lg:px-10 lg:py-28">
          <div className="max-w-3xl">
            <p className="font-mono text-xs uppercase tracking-[0.25em] text-white/80">
              Track Record
            </p>
            <h2 className="mt-4 font-display text-4xl leading-[1.05] md:text-5xl">
              Proof beats <em className="italic text-gradient">positioning.</em>
            </h2>
            <p className="mt-5 text-lg leading-relaxed text-white/80">
              A quick snapshot of the range behind the work. A decade of content strategy, technical
              marketing, and social work spanning AI infrastructure, SaaS, GPU compute, Web3, and
              cybersecurity, with hundreds of accounts managed, dozens of clients supported, and a
              creator platform built alongside the client work.
            </p>
          </div>
          <div className="mt-12 grid gap-5 sm:grid-cols-2 md:grid-cols-3">
            {STATS.map((s, i) => {
              const palette = [
                "var(--color-primary)",
                "var(--color-electric)",
                "var(--color-violet)",
                "var(--color-pink)",
              ];
              const c = palette[i % palette.length];
              return (
                <div
                  key={s.l}
                  className="group relative overflow-hidden rounded-md p-0.5 shadow-pop transition-all duration-300 hover:z-10 hover:scale-[1.03] hover:shadow-[0_28px_56px_-20px_oklch(0_0_0/0.6)]"
                >
                  <div
                    aria-hidden
                    className="absolute -inset-0.5 rounded-md opacity-0 transition-opacity duration-300 group-hover:opacity-100"
                    style={{
                      background:
                        "linear-gradient(180deg, var(--color-primary), var(--color-pink), var(--color-violet), var(--color-electric))",
                    }}
                  />
                  <div className="relative h-full overflow-hidden rounded-md border-2 border-white/90 bg-foreground/80 p-7 backdrop-blur-md transition-all duration-300 group-hover:border-white/70">
                    <div
                      aria-hidden
                      className="absolute -left-1 top-0 h-full w-1.5 transition-all duration-300 group-hover:opacity-0"
                      style={{ background: `linear-gradient(180deg, ${c}, transparent)` }}
                    />
                    <div className="relative z-10">
                      <div className="font-display text-5xl text-gradient">
                        <CountUp value={s.v} />
                      </div>
                      <div className="mt-3 font-mono text-[11px] uppercase tracking-[0.2em] text-background/90">
                        {s.l}
                      </div>
                      {s.note && (
                        <p className="mt-3 text-[12px] leading-snug text-background/90">{s.note}</p>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* DISCIPLINES — horizontal scroll ribbon */}
      <section className="relative isolate overflow-hidden border-b border-border bg-gradient-acid">
        <LightSectionBackdrop variant="blueprint" arcs={false} sparkles={false} />
        <div className="relative mx-auto max-w-7xl px-6 py-20 lg:px-10 lg:py-28">
          <div className="max-w-3xl">
            <p className="font-mono text-xs uppercase tracking-[0.25em] text-muted-foreground">
              Disciplines
            </p>
            <h2 className="mt-4 font-display text-4xl leading-[1.05] md:text-5xl">
              Built across more than <em className="italic text-gradient">one lane.</em>
            </h2>
            <p className="mt-5 text-lg leading-relaxed text-muted-foreground">
              The four headline services are the offers most teams hire me for. Under them sits a
              wider bench built from the disciplines I've developed across years of in-house,
              agency, and fractional work in technical markets.
            </p>
          </div>

          <div className="mt-14 grid grid-cols-1 gap-6 md:grid-cols-2">
            {/* Mobile: single column scrolling down */}
            <div
              aria-hidden="true"
              data-nosnippet
              className="marquee-col relative mx-3 h-[620px] overflow-hidden md:hidden"
            >
              <div
                style={{
                  maskImage:
                    "linear-gradient(to bottom, transparent, black 12%, black 88%, transparent)",
                  WebkitMaskImage:
                    "linear-gradient(to bottom, transparent, black 12%, black 88%, transparent)",
                }}
                className="absolute inset-0"
              />
              <div className="marquee-down flex flex-col gap-5 py-4 px-2">
                {[...MOBILE_DISCIPLINE_GROUPS, ...MOBILE_DISCIPLINE_GROUPS].map((g, idx) => {
                  const Icon = g.icon;
                  return (
                    <article
                      key={g.name + idx}
                      aria-hidden={idx >= MOBILE_DISCIPLINE_GROUPS.length ? "true" : undefined}
                      className="group relative min-h-[280px] overflow-hidden rounded-lg border-2 bg-background p-2 shadow-pop transition-all duration-300 ease-out hover:shadow-[0_22px_48px_-18px_oklch(0.16_0.02_280/0.5)]"
                      style={{ borderColor: g.color }}
                    >
                      <div className="relative flex h-full min-h-[264px] flex-col justify-center rounded-md bg-background p-6 transition-transform duration-300 ease-out group-hover:scale-[1.02]">
                        <div className="flex items-start gap-5">
                          <span
                            aria-hidden
                            className="flex h-16 w-16 shrink-0 items-center justify-center rounded-md border-2"
                            style={{
                              borderColor: g.color,
                              color: g.color,
                              background: `color-mix(in oklab, ${g.color} 12%, transparent)`,
                            }}
                          >
                            <Icon className="h-9 w-9 shrink-0" />
                          </span>
                          <div className="min-w-0 flex-1">
                            <h3
                              className="font-display text-2xl leading-tight md:text-3xl"
                              style={{ color: g.color }}
                            >
                              {g.name}
                            </h3>
                            <ul className="mt-4 flex flex-wrap gap-2">
                              {g.items.map((it) => (
                                <li
                                  key={it}
                                  className="rounded-full border px-3 py-1.5 font-mono text-xs uppercase tracking-[0.14em] text-foreground"
                                  style={{
                                    borderColor: `color-mix(in oklab, ${g.color} 35%, var(--color-border))`,
                                  }}
                                >
                                  {it}
                                </li>
                              ))}
                            </ul>
                          </div>
                        </div>
                      </div>
                    </article>
                  );
                })}
              </div>
            </div>

            {/* Desktop: two columns, one up, one down */}
            {[DISCIPLINE_GROUPS.slice(0, 3), DISCIPLINE_GROUPS.slice(3, 6)].map((col, ci) => (
              <div
                key={ci}
                className="marquee-col relative hidden h-[520px] overflow-hidden md:block"
                style={{
                  maskImage:
                    "linear-gradient(to bottom, transparent, black 12%, black 88%, transparent)",
                  WebkitMaskImage:
                    "linear-gradient(to bottom, transparent, black 12%, black 88%, transparent)",
                }}
              >
                <div
                  className={`flex flex-col gap-5 py-4 px-2 ${ci === 0 ? "marquee-up" : "marquee-down"}`}
                >
                  {[...col, ...col].map((g, idx) => {
                    const Icon = g.icon;
                    return (
                      <article
                        key={g.name + idx}
                        aria-hidden={idx >= col.length ? "true" : undefined}
                        {...(idx >= col.length ? { "data-nosnippet": "" } : {})}
                        className="group relative min-h-[280px] overflow-hidden rounded-lg border-2 bg-background p-2 shadow-pop transition-all duration-300 ease-out hover:shadow-[0_22px_48px_-18px_oklch(0.16_0.02_280/0.5)]"
                        style={{ borderColor: g.color }}
                      >
                        <div className="relative flex h-full min-h-[264px] flex-col justify-center rounded-md bg-background p-6 transition-transform duration-300 ease-out group-hover:scale-[1.02]">
                          <div className="flex items-start gap-5">
                            <span
                              aria-hidden
                              className="flex h-16 w-16 shrink-0 items-center justify-center rounded-md border-2"
                              style={{
                                borderColor: g.color,
                                color: g.color,
                                background: `color-mix(in oklab, ${g.color} 12%, transparent)`,
                              }}
                            >
                              <Icon className="h-9 w-9 shrink-0" />
                            </span>
                            <div className="min-w-0 flex-1">
                              <h3
                                className="font-display text-2xl leading-tight md:text-3xl"
                                style={{ color: g.color }}
                              >
                                {g.name}
                              </h3>
                              <ul className="mt-4 flex flex-wrap gap-2">
                                {g.items.map((it) => (
                                  <li
                                    key={it}
                                    className="rounded-full border px-3 py-1.5 font-mono text-xs uppercase tracking-[0.14em] text-foreground"
                                    style={{
                                      borderColor: `color-mix(in oklab, ${g.color} 35%, var(--color-border))`,
                                    }}
                                  >
                                    {it}
                                  </li>
                                ))}
                              </ul>
                            </div>
                          </div>
                        </div>
                      </article>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* HOW I WORK — circular process */}
      <section className="relative isolate overflow-hidden border-b border-border bg-gradient-night text-white">
        <DarkSectionBackdrop />
        <HowIWorkBackdrop />

        <div className="relative mx-auto max-w-7xl px-6 pt-28 pb-10 sm:px-8 lg:px-10 lg:pt-40 lg:pb-20">
          <div className="max-w-5xl">
            <p className="font-mono text-xs uppercase tracking-[0.25em] text-white/80">
              How I Work
            </p>
            <h2 className="mt-4 font-display text-4xl leading-[1.05] md:text-5xl">
              Content systems over <em className="italic text-gradient">random posting.</em>
            </h2>
            <div className="mt-5 space-y-5 text-lg leading-relaxed text-white/80">
              <p>
                I like content that has a job. That means clearer positioning, better distribution,
                stronger execution, cleaner reporting, and more useful workflows.
              </p>
              <p>
                I'm not interested in content for content's sake. The best work makes a complicated
                idea easier to understand, easier to trust, and easier to act on. That means
                building systems, not just publishing posts.
              </p>
            </div>
          </div>

          <HowIWorkOrbit />

        </div>
      </section>

      {/* FAQ */}
      <section
        id="faq"
        className="relative isolate overflow-hidden border-t border-border scroll-mt-24"
      >
        <LightSectionBackdrop variant="dots" arcs={false} sparkles={false} />
        <div className="relative mx-auto max-w-4xl px-6 py-20 lg:px-10 lg:py-24">
          <p className="font-mono text-xs uppercase tracking-[0.25em] text-muted-foreground">
            FAQ
          </p>
          <h2 className="mt-4 font-display text-4xl leading-[1.05] md:text-5xl">
            Questions about <em className="italic text-gradient">Scott Cunningham</em>
          </h2>
          <div className="mt-10 grid gap-4">
            {ABOUT_FAQ.map((f) => (
              <details
                key={f.q}
                className="faq-card group relative overflow-hidden rounded-md border-2 border-foreground bg-background shadow-pop transition-all duration-300 hover:-translate-y-0.5 hover:shadow-[0_22px_48px_-18px_oklch(0.16_0.02_280/0.5)]"
              >
                <summary className="flex cursor-pointer list-none items-center justify-between gap-4 p-5 md:p-6">
                  <h3 className="faq-question font-display text-xl leading-snug">{f.q}</h3>
                  <span
                    aria-hidden
                    className="faq-icon grid h-9 w-9 shrink-0 place-items-center rounded-full border-2 border-foreground text-foreground transition-all duration-300 group-open:rotate-45"
                  >
                    <Plus className="h-4 w-4" />
                  </span>
                </summary>
                <div className="px-5 pb-5 md:px-6 md:pb-6">
                  <p className="text-base leading-relaxed text-muted-foreground">{f.aJsx ?? f.a}</p>
                </div>
              </details>
            ))}
          </div>
        </div>
      </section>

      <SparkleDivider />

      {/* CONNECT / LINKS */}
      <section id="links" className="relative isolate overflow-hidden scroll-mt-24">
        <LightSectionBackdrop variant="analytics" arcs={false} sparkles={false} />
        <div className="relative mx-auto max-w-7xl px-6 py-20 lg:px-10 lg:py-28">
          <div className="grid gap-10 md:grid-cols-12">
            <div className="md:col-span-5">
              <p className="font-mono text-xs uppercase tracking-[0.25em] text-muted-foreground">
                Connect
              </p>
              <h2 className="mt-4 font-display text-4xl leading-[1.05] md:text-5xl">
                All my links in <em className="italic text-gradient">one place.</em>
              </h2>
            </div>
            <p className="md:col-span-7 text-lg leading-relaxed text-muted-foreground md:pt-12">
              Find me across the platforms I actually use. Best places to follow new work, episodes,
              and field notes.
            </p>
          </div>
          <AllLinksGrid />
        </div>
      </section>

      <SparkleDivider />

      {/* CTA */}
      <section className="relative isolate overflow-hidden">
        <div className="absolute inset-0 -z-10 bg-gradient-acid" />
        <LightSectionBackdrop variant="blueprint" arcs={false} sparkles={false} />
        <div className="mx-auto max-w-5xl px-6 py-20 lg:px-10 lg:py-28">
          <div className="relative overflow-hidden rounded-lg border-2 border-foreground bg-background p-10 shadow-pop md:p-14">
            <div
              aria-hidden
              className="absolute -right-24 -top-24 h-72 w-72 rounded-full bg-[var(--color-primary)] opacity-20 blur-3xl"
            />
            <div
              aria-hidden
              className="absolute -left-24 -bottom-24 h-72 w-72 rounded-full bg-[var(--color-electric)] opacity-20 blur-3xl"
            />
            <p className="font-mono text-xs uppercase tracking-[0.25em] text-muted-foreground">
              Let's Work Together
            </p>
            <h2 className="mt-4 max-w-3xl font-display text-4xl leading-[1.05] md:text-5xl">
              Need clearer content for a <em className="italic text-gradient">complex brand?</em>
            </h2>
            <p className="mt-5 max-w-2xl text-lg leading-relaxed text-muted-foreground">
              If you're building in AI, cloud, Web3, cybersecurity, SaaS, or technical markets, I
              can help turn the message into a system people actually understand.
            </p>
            <div className="mt-8 flex flex-wrap items-center gap-3">
              <Link
                to="/contact"
                className="lift-glow inline-flex items-center gap-2 rounded-full border-2 border-foreground bg-foreground px-5 py-2.5 font-mono text-xs uppercase tracking-[0.2em] text-background hover:bg-background hover:text-foreground"
              >
                Work With Me <ArrowUpRight className="h-4 w-4" />
              </Link>
              <Link
                to="/work"
                className="inline-flex items-center gap-2 rounded-full border-2 border-foreground bg-background px-5 py-2.5 font-mono text-xs uppercase tracking-[0.2em] text-foreground hover:bg-foreground hover:text-background"
              >
                View Work <ArrowUpRight className="h-4 w-4" />
              </Link>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
